// import components
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, IconButtonProps, IconButton, Tooltip } from '@mui/material';

// import hooks
import {
  useGridApiContext,
  useGridSelector,
  gridFilteredDescendantCountLookupSelector
} from '@mui/x-data-grid-pro';

// import types
import { DutyLineSummaryGridRenderCellParams } from './DutyLineSummaryGrid.types';

// import utils
import {
  getChildrenExpanded,
  isGroupOrLeafNode,
  isNavigationKey
} from './DutyLineSummaryGrid.utils';
import { Icon } from '@sdb/planning-ui';

export const CustomGridTreeDataGroupingCell = (
  props: DutyLineSummaryGridRenderCellParams
) => {
  const { id, field, rowNode, tooltips } = props;

  if (!isGroupOrLeafNode(rowNode)) {
    return null;
  }

  const apiRef = useGridApiContext();
  const filteredDescendantCountLookup = useGridSelector(
    apiRef,
    gridFilteredDescendantCountLookupSelector
  );

  const expanded = getChildrenExpanded(rowNode);

  const filteredDescendantCount =
    filteredDescendantCountLookup[rowNode.id] ?? 0;

  const handleKeyDown: IconButtonProps['onKeyDown'] = (event) => {
    if (event.key === ' ') {
      event.stopPropagation();
    }
    if (isNavigationKey(event.key) && !event.shiftKey) {
      apiRef.current.publishEvent('cellKeyDown', props, event);
    }
  };

  const handleClick: IconButtonProps['onClick'] = (event) => {
    apiRef.current.setRowChildrenExpansion(id, !expanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  const tooltip = tooltips.find(
    ({ groupingKey }) => groupingKey === rowNode.groupingKey
  );

  return (
    <Box sx={{ ml: rowNode.depth * 2 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <IconButton
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          size="small"
          sx={{
            visibility: filteredDescendantCount > 0 ? 'visible' : 'hidden'
          }}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Box display="flex" alignItems="center" gap={1}>
          {rowNode.groupingKey}
          {tooltip && (
            <Tooltip title={tooltip.title}>
              <Icon type="circle-info" fontSize="inherit" />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};
